import { useCallback } from 'react'
import { GetContextMenuItems, GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise'
import type { DefaultMenuItem } from 'ag-grid-community'
import { useSelector } from 'react-redux'
import i18next from 'i18next'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import { StatementColumn } from '../types'
import { isVoucherAvailable } from '../utils/columnUtils'
import { useModalActions } from '../components/StatementTable/ModalContext'
import { useNavigateToDriverData } from '../../budgetingMethod/hooks/useNavigateToDriverData'
import { useNavigateToAllAccountDrivers } from '../../budgetingMethod/hooks/useNavigateToAllAccountDrivers'
import { isRowCommentable } from '../../budgetComments/utils/commentUtils'
import { contextCompanyIdSelector, contextCompanySelector } from '../../../redux/context/company/selectors'
import { useAccountMapRules } from '../../accountMap/queries/useAccountMapRules'

/**
 * Hook for creating context menu items for financial statement tables
 */
export const useStatementContextMenu = (): GetContextMenuItems<EnhancedStatementRow> => {
  const companyId = useSelector(contextCompanyIdSelector)
  const accountingSoftware = useSelector(contextCompanySelector)?.accountingSoftware

  // Pre-load account map rules to ensure they're available for voucher modal
  useAccountMapRules({ companyId: companyId! })

  // Use useModalActions() instead of useModal() to avoid re-renders when modal state changes
  const { openCommentModal, openVoucherModal } = useModalActions()
  const navigateToDriverData = useNavigateToDriverData()
  const navigateToAllAccountDrivers = useNavigateToAllAccountDrivers()

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<EnhancedStatementRow>): (DefaultMenuItem | MenuItemDef)[] => {
      const defaultItems: DefaultMenuItem[] = ['copy', 'copyWithHeaders', 'paste']

      if (!params.node?.data) {
        return defaultItems
      }

      const row = params.node.data
      const menuItems: (MenuItemDef | DefaultMenuItem)[] = []

      // Get column data for menu items that need it
      const columnData = params.column
        ? ((params.column.getColDef().headerComponentParams as any)?.columnData as StatementColumn | undefined)
        : undefined

      // Add driver data menu item if row has driver-based budgeting method
      if (row.budgetingMethod?.method === 'driver') {
        const driverDataMenuItem: MenuItemDef = {
          name: i18next.t('financialStatementsPage:row-drivers', 'Rivin ajurit'),
          action: () => {
            navigateToDriverData(row)
          },
          icon: '<i class="ag-icon-edit" aria-hidden="true"></i>'
        }
        menuItems.push(driverDataMenuItem)
      }

      // Add "All account drivers" menu item (shows all unique drivers from all accounts)
      const allAccountDriversMenuItem: MenuItemDef = {
        name: i18next.t('financialStatementsPage:all-account-drivers', 'Laskentakohteen ajurit'),
        action: () => {
          navigateToAllAccountDrivers(params.api)
        },
        icon: '<i class="ag-icon-group" aria-hidden="true"></i>'
      }
      menuItems.push(allAccountDriversMenuItem)

      // Add voucher menu item if available
      if (columnData && isVoucherAvailable(row, columnData, accountingSoftware)) {
        const voucherMenuItem: MenuItemDef = {
          name: i18next.t('global:vouchers'),
          action: () => {
            openVoucherModal({ row, column: columnData })
          },
          icon: '<i class="fa fa-file-text-o" aria-hidden="true"></i>'
        }
        menuItems.push(voucherMenuItem)
      }

      // Add comment menu item if we have column data and row is commentable
      if (columnData && columnData?.columnType !== 'actuals' && isRowCommentable(row)) {
        const commentMenuItem: MenuItemDef = {
          name: i18next.t('global:comments'),
          action: () => {
            openCommentModal({ row, column: columnData })
          },
          icon: '<i class="fa fa-comment-o" aria-hidden="true"></i>'
        }
        menuItems.push(commentMenuItem)
      }

      // Add separator if we have any custom items
      if (menuItems.length > 0) {
        menuItems.push('separator' as DefaultMenuItem)
      }

      return [...menuItems, ...defaultItems]
    },
    [navigateToDriverData, navigateToAllAccountDrivers, openVoucherModal, openCommentModal, accountingSoftware]
  )

  return getContextMenuItems
}
